import { Injectable, signal } from '@angular/core';

export interface UtilApiCredentials {
  apiBaseUrl: string;
  token: string;
}

const STORAGE_KEY = 'jk-util.api-auth.v1';

export function normalizeApiBaseUrl(value: string, documentUrl: string): string {
  if (!/^https?:\/\//i.test(value) && !value.startsWith('/')) {
    throw new Error('請輸入完整 http(s) API 網址或 / 開頭的代理路徑。');
  }
  const url = new URL(value, documentUrl);
  if (!['http:', 'https:'].includes(url.protocol) || url.username || url.password || url.search || url.hash) {
    throw new Error('API 網址不可包含帳密、查詢參數或片段。');
  }
  return `${url.origin}${url.pathname.replace(/\/+$/, '')}`;
}

export function tokenForUrl(credentials: UtilApiCredentials | null, requestUrl: string, documentUrl: string): string | null {
  if (!credentials) return null;
  try {
    const target = new URL(requestUrl, documentUrl);
    const api = new URL(credentials.apiBaseUrl);
    const path = api.pathname.replace(/\/+$/, '');
    return !target.username && !target.password && target.origin === api.origin
      && (target.pathname === path || target.pathname.startsWith(`${path}/`))
      ? credentials.token : null;
  } catch {
    return null;
  }
}

@Injectable({ providedIn: 'root' })
export class UtilApiAuthService {
  readonly credentials = signal<UtilApiCredentials | null>(this.restore());
  readonly unauthorized = signal(false);

  save(apiBaseUrl: string, token: string): void {
    const value: UtilApiCredentials = {
      apiBaseUrl: normalizeApiBaseUrl(apiBaseUrl.trim(), document.baseURI),
      token: token.trim(),
    };
    if (!/^[A-Za-z0-9._~+/-]{32,}=*$/.test(value.token)) {
      throw new Error('請輸入至少 32 個字元的 token，不含 Bearer 前綴。');
    }
    this.credentials.set(value);
    this.unauthorized.set(false);
    try { sessionStorage.setItem(STORAGE_KEY, JSON.stringify(value)); } catch { /* Use memory if storage is disabled. */ }
  }

  clear(): void {
    this.credentials.set(null);
    this.unauthorized.set(false);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch { /* Storage may be disabled. */ }
  }

  private restore(): UtilApiCredentials | null {
    try {
      const saved: unknown = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || 'null');
      if (typeof saved !== 'object' || saved === null || !('apiBaseUrl' in saved) || !('token' in saved)
        || typeof saved.apiBaseUrl !== 'string' || typeof saved.token !== 'string'
        || !/^[A-Za-z0-9._~+/-]{32,}=*$/.test(saved.token)) return null;
      return { apiBaseUrl: normalizeApiBaseUrl(saved.apiBaseUrl, document.baseURI), token: saved.token };
    } catch {
      return null;
    }
  }
}
